import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import NextImage from '@base/Image';
import NotifyMe from '@icon/PlatformBatchGroup/NotifyMe';
import NotifiedMe from '@icon/PlatformBatchGroup/NotifiedMe';
import NotifiedMeDarkMode from '@icon/PlatformBatchGroup/NotifiedMeDarkMode';
import NotifyMeWhiteIcon from '@icon/PlatformBatchGroup/NotifyMeWhiteIcon';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useNotifyMe from '../useNotifyMe';
import notificationsType from '../constants';
import {
  Wrapper,
  Info,
  Details,
  Title,
  ButtonWrapper,
  IconWrapper,
  NotifyWrapper,
  NotifiedMeCTA
} from './WebNotifyMe';

export const StyledNotifiedMeCTA = styled(NotifiedMeCTA)`
  background-color: ${({ $isNotified }) =>
    $isNotified ? 'var(--color-divider)' : 'var(--color-base-1)'};
  color: var(--color-text-primary);
  padding: ${Spacings.SPACING_0B};
  font-size: ${Fonts.SMALL};
  height: 48px;
  width: 202px;
  border-radius: ${Spacings.SPACING_2B};
  &:active {
    background-color: var(--color-i-black);
  }
  ${mediaQuery.uptoMobile} {
    div {
      align-items: center;
      justify-content: center;
      display: flex;
      margin-right: ${Spacings.SPACING_1B};
    }
    height: 32px;
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: ${Spacings.SPACING_4B};
    width: 132px;
    min-height: 32px;
    border-radius: ${Spacings.SPACING_1B};
  }
`;

const getIcon = ({ isNotified, isDarkModeOn }) => {
  if (isNotified && isDarkModeOn)
    return <NotifiedMeDarkMode size="24px" color="var(--color-base-1)" />;
  if (isNotified && !isDarkModeOn)
    return <NotifiedMe size="24px" color="var(--color-base-1)" />;
  if (!isNotified && isDarkModeOn)
    return <NotifyMe size="24px" color="var(--color-base-1)" />;
  if (!isNotified && !isDarkModeOn)
    return <NotifyMeWhiteIcon size="24px" color="var(--color-base-1)" />;
  return null;
};

const OnGoingEnrollmentBatchesNotifyMe = () => {
  const {
    sendNotifyMeRequest,
    notifiedNotification = {},
    notifyMeInfo,
    isNotifyRequested,
    isNotificationFetched
  } = useNotifyMe();
  const { onGoingBatches } = notifyMeInfo;
  const isNotified = notifiedNotification[notificationsType.ONGOING_BATCHES];
  const handleClick = useCallback(() => {
    if (isNotifyRequested) return null;
    sendNotifyMeRequest({
      notification_type: notificationsType.ONGOING_BATCHES,
      is_active: !isNotified,
      touchPoint: 'Page Header'
    });
  }, [isNotified, isNotifyRequested, sendNotifyMeRequest]);

  return (
    <NotifyWrapper>
      <Wrapper>
        <IconWrapper>
          <NextImage src="special-class.svg" alt="notify-me" layout="fill" />
        </IconWrapper>
        <Details>
          <Title>{onGoingBatches.title}</Title>
          <Info>{onGoingBatches.subtitle}</Info>
        </Details>
        {isNotificationFetched && (
          <ButtonWrapper>
            <NotifiedMeCTAWrapper
              isNotified={isNotified}
              onClick={handleClick}
            />
          </ButtonWrapper>
        )}
      </Wrapper>
    </NotifyWrapper>
  );
};
export default OnGoingEnrollmentBatchesNotifyMe;

export const NotifiedMeCTAWrapper = ({ onClick, isNotified }) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const icon = useMemo(
    () => getIcon({ isNotified, isDarkModeOn }),
    [isNotified, isDarkModeOn]
  );
  return (
    <StyledNotifiedMeCTA
      $secondary
      $isNotified={isNotified}
      label={isNotified ? 'We’ll notify you' : 'Notify me'}
      variant="hollow"
      onClick={onClick}
      icon={icon}
    />
  );
};
